<template>
  <div class="panel panel-default">

    <!-- ── Controls ──────────────────────────────────────────────── -->
    <div class="controls-bar">
      <div class="control-card">
        <label class="control-label">Difficulty</label>
        <div class="pill-group">
          <button
            v-for="d in difficulties"
            :key="d.label"
            class="pill-btn"
            :class="{ active: difficulty === d.label }"
            @click="setDifficulty(d)"
          >{{ d.label }}</button>
        </div>
      </div>
      <div class="control-card">
        <label class="control-label">Type</label>
        <div class="pill-group">
          <button
            v-for="t in types"
            :key="t"
            class="pill-btn"
            :class="{ active: typeFilter === t }"
            @click="setType(t)"
          >{{ t }}</button>
        </div>
      </div>
      <div class="control-card">
        <label class="control-label">Mode</label>
        <div class="pill-group">
          <button
            class="pill-btn"
            :class="{ active: inputMode === 'choice' }"
            @click="setMode('choice')"
          >Multiple Choice</button>
          <button
            class="pill-btn"
            :class="{ active: inputMode === 'typein' }"
            @click="setMode('typein')"
          >Type Answer</button>
        </div>
      </div>
    </div>

    <!-- ── Op badge (hidden for word problems) ───────────────────── -->
    <div v-if="!isWordProblem" class="text-center" style="margin-bottom: 10px;">
      <span class="type-badge" :class="opBadgeClass">
        {{ currentOp === 'multiplication' ? '× Multiplication' : '÷ Division' }}
      </span>
    </div>

    <!-- ── Expert decimal note ────────────────────────────────────── -->
    <div v-if="difficulty === 'Expert'" class="text-center expert-note">
      Expert includes large numbers &amp; decimals. Type-in accepts decimals.
    </div>

    <!-- ── Correct banner ─────────────────────────────────────────── -->
    <transition name="fade">
      <div v-if="answered" class="feedback feedback-correct text-center">
        <span class="feedback-text">✓ Correct! Well done!</span>
        <div class="progress-bar-wrap">
          <div class="progress-bar-fill" :style="{ width: progressWidth + '%' }"></div>
        </div>
      </div>
    </transition>

    <!-- ── Wrong banner ───────────────────────────────────────────── -->
    <transition name="fade">
      <div v-if="wrongAnswer" class="feedback feedback-wrong text-center">
        <span class="feedback-text">✗ Wrong! Try again.</span>
      </div>
    </transition>

    <!-- ── Question ───────────────────────────────────────────────── -->
    <div class="panel-heading">
      <h3 class="panel-title text-center" :class="{ 'word-problem-q': isWordProblem }">
        {{ question }}
      </h3>
    </div>

    <!-- ── Multiple choice ────────────────────────────────────────── -->
    <div class="panel-body answer-grid" v-if="inputMode === 'choice'">
      <button
        v-for="(btn, index) in btnData"
        :key="index"
        class="ans-btn"
        :disabled="answered"
        @click="onAnswer(btn.correct)"
      >
        {{ btn.answer }}
      </button>
    </div>

    <!-- ── Type-in ────────────────────────────────────────────────── -->
    <div class="panel-body typein-body" v-if="inputMode === 'typein'">
      <div class="typein-row">
        <input
          ref="typeinInput"
          :type="difficulty === 'Expert' ? 'text' : 'number'"
          class="typein-input"
          v-model="userInput"
          :disabled="answered"
          placeholder="Your answer"
          @keyup.enter="checkTypein"
        />
        <button class="btn btn-primary typein-btn" :disabled="answered" @click="checkTypein">Check</button>
      </div>
      <transition name="fade">
        <p v-if="typeinFeedback" class="typein-feedback" :class="typeinFeedbackClass">
          {{ typeinFeedback }}
        </p>
      </transition>
    </div>

    <!-- ── Next Question ─────────────────────────────────────────── -->
    <div class="text-center next-btn-wrap">
      <button class="next-btn" @click="nextQuestion">Next Question →</button>
    </div>

  </div>
</template>

<script>
import { MUL_WORD, DIV_WORD } from './wordProblemsForTimesDivide.js';

// ─── Helpers ──────────────────────────────────────────────────────────────
function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randDec(min, max, dp = 1) {
  const factor = Math.pow(10, dp);
  return Math.round((Math.random() * (max - min) + min) * factor) / factor;
}

function round(n, dp = 2) {
  return Math.round(n * Math.pow(10, dp)) / Math.pow(10, dp);
}

function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const DIFFICULTIES = [
  { label: "Easy",   mulMin: 1,  mulMax: 6,  divMax: 6,  decimals: false },
  { label: "Medium", mulMin: 1,  mulMax: 10, divMax: 10, decimals: false },
  { label: "Hard",   mulMin: 1,  mulMax: 12, divMax: 12, decimals: false },
  { label: "Expert", mulMin: 10, mulMax: 99, divMax: 25, decimals: true  },
];

const PROGRESS_DURATION = 1000;
const PROGRESS_INTERVAL = 30;

export default {
  data() {
    return {
      difficulties: DIFFICULTIES,
      difficulty:   "Easy",
      mulMin:       1,
      mulMax:       6,
      divMax:       6,
      useDecimals:  false,

      types:      ["Mixed", "Multiplication", "Division", "Word Problems"],
      typeFilter: "Mixed",

      inputMode: "choice",

      question:      "",
      currentOp:     "multiplication",
      correctAnswer: 0,
      isWordProblem: false,

      btnData: [],

      answered:      false,
      wrongAnswer:   false,
      progressWidth: 0,

      userInput:           "",
      typeinFeedback:      "",
      typeinFeedbackClass: "",

      _progressTimer: null,
      _autoTimer:     null,
    };
  },

  computed: {
    opBadgeClass() {
      return this.currentOp === "multiplication" ? "badge-times" : "badge-div";
    },
  },

  methods: {
    setDifficulty(d) {
      this.difficulty  = d.label;
      this.mulMin      = d.mulMin;
      this.mulMax      = d.mulMax;
      this.divMax      = d.divMax;
      this.useDecimals = d.decimals;
      this.generateQuestion();
    },

    setType(t) {
      this.typeFilter = t;
      this.generateQuestion();
    },

    setMode(m) {
      this.inputMode      = m;
      this.userInput      = "";
      this.typeinFeedback = "";
      this.generateQuestion();
    },

    generateQuestion() {
      this.answered       = false;
      this.wrongAnswer    = false;
      this.progressWidth  = 0;
      this.userInput      = "";
      this.typeinFeedback = "";
      this.isWordProblem  = false;
      clearTimeout(this._autoTimer);
      clearInterval(this._progressTimer);

      const isWordMode = this.typeFilter === "Word Problems";
      let useMultiplication;

      if (this.typeFilter === "Multiplication") {
        useMultiplication = true;
      } else if (this.typeFilter === "Division") {
        useMultiplication = false;
      } else {
        useMultiplication = Math.random() < 0.5;
      }

      this.currentOp = useMultiplication ? "multiplication" : "division";

      let a, b, correct;

      if (useMultiplication) {
        if (this.useDecimals && !isWordMode) {
          a = randDec(this.mulMin, this.mulMax, 1);
          b = randInt(2, 20);
          correct = round(a * b, 2);
        } else {
          a = randInt(this.mulMin, this.mulMax);
          b = randInt(this.mulMin, this.mulMax);
          correct = a * b;
        }
      } else {
        b       = randInt(2, this.divMax);
        correct = randInt(2, this.divMax);
        a       = b * correct;
      }

      this.correctAnswer = correct;

      if (isWordMode) {
        this.isWordProblem = true;
        if (useMultiplication) {
          const wa = randInt(this.mulMin, this.mulMax);
          const wb = randInt(2, Math.min(this.mulMax, 20));
          this.correctAnswer = wa * wb;
          const tmpl    = MUL_WORD[randInt(0, MUL_WORD.length - 1)];
          this.question = tmpl(wa, wb);
        } else {
          const wDiv = randInt(2, this.divMax);
          const wQuo = randInt(2, this.divMax);
          const wDvd = wDiv * wQuo;
          this.correctAnswer = wQuo;
          const tmpl    = DIV_WORD[randInt(0, DIV_WORD.length - 1)];
          this.question = tmpl(wDvd, wDiv);
        }
      } else {
        this.question = useMultiplication
          ? `What is ${a} × ${b}?`
          : `What is ${a} ÷ ${b}?`;
      }

      const ans = this.correctAnswer;

      if (this.useDecimals && useMultiplication && !isWordMode) {
        const spread = Math.max(0.5, round(Math.abs(ans) * 0.1, 1));
        const used   = new Set([ans]);
        const wrongs = [];
        while (wrongs.length < 3) {
          const offset    = round(Math.random() * spread * 2 - spread, 1);
          const candidate = round(ans + offset, 2);
          if (candidate !== ans && candidate > 0 && !used.has(candidate)) {
            used.add(candidate);
            wrongs.push(candidate);
          }
        }
        this.btnData = shuffle([
          { correct: true,  answer: ans },
          ...wrongs.map(w => ({ correct: false, answer: w })),
        ]);
      } else {
        const spread = Math.max(3, Math.round(Math.abs(ans) * 0.3));
        const used   = new Set([ans]);
        const wrongs = [];
        while (wrongs.length < 3) {
          const offset    = randInt(-spread, spread);
          const candidate = ans + offset;
          if (candidate !== ans && candidate > 0 && !used.has(candidate)) {
            used.add(candidate);
            wrongs.push(candidate);
          }
        }
        this.btnData = shuffle([
          { correct: true,  answer: ans },
          ...wrongs.map(w => ({ correct: false, answer: w })),
        ]);
      }

      if (this.inputMode === "typein") {
        this.$nextTick(() => {
          if (this.$refs.typeinInput) this.$refs.typeinInput.focus();
        });
      }
    },

    onCorrect() {
      this.wrongAnswer   = false;
      this.answered      = true;
      this.progressWidth = 0;
      const step = (PROGRESS_INTERVAL / PROGRESS_DURATION) * 100;
      this._progressTimer = setInterval(() => {
        this.progressWidth = Math.min(this.progressWidth + step, 100);
      }, PROGRESS_INTERVAL);
      this._autoTimer = setTimeout(() => {
        clearInterval(this._progressTimer);
        this.generateQuestion();
      }, PROGRESS_DURATION);
    },

    nextQuestion() {
      clearInterval(this._progressTimer);
      clearTimeout(this._autoTimer);
      this.generateQuestion();
    },

    onAnswer(isCorrect) {
      if (isCorrect) {
        this.onCorrect();
      } else {
        this.wrongAnswer = true;
      }
    },

    checkTypein() {
      if (this.answered) return;
      const raw = this.userInput.trim();
      const val = parseFloat(raw);
      if (isNaN(val)) {
        this.typeinFeedback      = "Please enter a number.";
        this.typeinFeedbackClass = "feedback-wrong";
        return;
      }
      if (Math.abs(val - this.correctAnswer) < 0.001) {
        this.typeinFeedback = "";
        this.onCorrect();
      } else {
        this.wrongAnswer    = true;
        this.typeinFeedback = "";
        this.userInput      = "";
        this.$nextTick(() => {
          if (this.$refs.typeinInput) this.$refs.typeinInput.focus();
        });
      }
    },
  },

  created() {
    this.generateQuestion();
  },

  beforeDestroy() {
    clearTimeout(this._autoTimer);
    clearInterval(this._progressTimer);
  },
};
</script>

<style scoped>
/* ── Controls ─────────────────────────────────────────────────────── */
.controls-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  padding: 14px 10px 10px;
  border-bottom: 1px solid rgba(0,0,0,0.08);
  margin-bottom: 14px;
}
.control-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid rgba(3, 53, 48, 0.18);
  border-radius: 10px;
  padding: 10px 14px 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  min-width: 120px;
}
.control-label {
  font-size: 12px;
  font-weight: bold;
  color: rgb(3, 53, 48);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.pill-group {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
}
.pill-btn {
  border-radius: 20px;
  padding: 3px 14px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid rgb(3, 53, 48);
  background: transparent;
  color: rgb(3, 53, 48);
  transition: all 0.15s;
}
.pill-btn:hover  { background: rgb(169, 247, 234); }
.pill-btn.active { background: rgb(3, 53, 48); color: white; border-color: rgb(3, 53, 48); }

/* ── Op badge ─────────────────────────────────────────────────────── */
.type-badge {
  display: inline-block;
  padding: 3px 18px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
}
.badge-times {
  background: rgb(169, 247, 234);
  color: rgb(3, 80, 50);
  border: 1px solid rgb(3, 120, 70);
}
.badge-div {
  background: rgb(220, 210, 255);
  color: rgb(60, 20, 140);
  border: 1px solid rgb(100, 60, 200);
}

/* ── Expert note ──────────────────────────────────────────────────── */
.expert-note {
  font-size: 13px;
  color: rgba(3, 53, 48, 0.65);
  margin-bottom: 8px;
}

/* ── Feedback banners ─────────────────────────────────────────────── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

.feedback {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 17px;
  font-weight: bold;
}
.feedback-correct {
  background: rgba(3, 120, 60, 0.12);
  color: rgb(3, 100, 50);
  border: 2px solid rgba(3, 120, 60, 0.3);
  flex-direction: column;
  gap: 10px;
}
.feedback-wrong {
  background: rgba(180, 30, 30, 0.1);
  color: rgb(160, 20, 20);
  border: 2px solid rgba(180, 30, 30, 0.3);
}
.feedback-text { font-size: 1.1rem; }

/* ── Progress bar ─────────────────────────────────────────────────── */
.progress-bar-wrap {
  width: 100%;
  height: 6px;
  background: rgba(3, 120, 60, 0.2);
  border-radius: 4px;
  overflow: hidden;
}
.progress-bar-fill {
  height: 100%;
  background: rgb(3, 120, 60);
  border-radius: 4px;
  transition: width 30ms linear;
}

/* ── Word problem question ────────────────────────────────────────── */
.word-problem-q {
  font-size: 16px !important;
  line-height: 1.6;
  font-weight: 500;
  color: rgb(3, 53, 48);
  padding: 16px 8px 8px;
  text-align: left !important;
  white-space: normal;
}

/* ── Answer grid ──────────────────────────────────────────────────── */
.answer-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 12px 16px 16px;
}
.ans-btn {
  min-height: 56px;
  font-size: 18px;
  font-weight: 700;
  border-radius: 8px;
  border: 2px solid rgb(3, 53, 48);
  background: rgb(169, 247, 234);
  color: rgb(3, 53, 48);
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  word-break: break-all;
}
.ans-btn:hover:not(:disabled) {
  background: rgb(100, 225, 205);
  transform: scale(1.03);
}
.ans-btn:disabled { opacity: 0.55; cursor: default; }

/* ── Type-in ─────────────────────────────────────────────────────── */
.typein-body  { padding: 20px 10px; }
.typein-row   { display: flex; justify-content: center; align-items: center; gap: 10px; flex-wrap: wrap; }
.typein-input {
  width: 160px; height: 48px; font-size: 22px; text-align: center;
  border: 2px solid rgb(3, 53, 48); border-radius: 8px; outline: none; padding: 4px 8px;
}
.typein-input:focus { border-color: rgb(3, 120, 70); background: #f0fdf8; }
.typein-btn   { height: 48px; padding: 0 20px; font-size: 16px; }
.typein-feedback { text-align: center; font-size: 16px; font-weight: bold; margin-top: 12px; }

/* ── Next Question ────────────────────────────────────────────────── */
.next-btn-wrap {
  margin-top: 16px;
  margin-bottom: 10px;
}
.next-btn {
  background-color: rgb(3, 53, 48);
  color: white;
  padding: 10px 28px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  white-space: nowrap;
}
.next-btn:hover { background-color: rgb(5, 80, 70); }
</style>
